import { useEffect, useMemo, useState } from 'react';
import { WandSparklesIcon } from 'lucide-react';
import { Panel } from '../ui/Panel';
import { Field } from '../ui/Field';
import { EmptyState } from '../ui/EmptyState';
import { PlanItemRow } from './PlanItemRow';
import { undoToast } from '../ui/undoToast';
import { useAdmin } from '../AdminContext';
import { btnPrimary, btnSecondary, btnGhost, inputCls } from '../ui/styles';
import { clone, move } from '../editing';
import { effectivePlan, planItemFor, sortPlanItems } from '../../utils/account';
import { today } from '../../core/format/dates';
import { useEditBuffer } from '../../hooks/useEditBuffer';
import type { PlanItem, SkinPlan } from '../../core/types/account';
import type { CustomerFileControl } from './useCustomerFile';

/**
 * Het persoonlijke huidplan, zoals de klant het in Mijn omgeving ziet: de
 * volgorde, ochtend/avond, Sarahs instructie en hoe lang een flesje meegaat.
 */
export function PlanEditor({
  ctl,
  onDirtyChange
}: {
  ctl: CustomerFileControl;
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const {
    catalog,
    index
  } = useAdmin();
  const {
    account
  } = ctl.file;
  const [saving, setSaving] = useState(false);
  // Niets aangepast = het plan van de server; wel aangepast = blijft staan bij verversen en bij een
  // mislukte opslag (die zette het plan eerst terug en wiste zo de invoer). hooks/useEditBuffer.ts
  const buffer = useEditBuffer<SkinPlan>(account.plan, account.customer.id);
  const draft = buffer.value;
  const dirty = buffer.dirty;
  // Het dossier waarschuwt bij een ander tabblad zolang er niet-bewaarde wijzigingen zijn.
  useEffect(() => onDirtyChange?.(dirty), [dirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);
  const provisional = useMemo(() => effectivePlan({
    ...account,
    plan: {
      ...account.plan,
      items: []
    }
  }, index.productById), [account, index]);
  const routine = draft.routineId ? index.routineById(draft.routineId) : undefined;
  const missingFromRoutine = routine ? routine.productIds.filter(id => !draft.items.some(i => i.productId === id)) : [];
  const addable = catalog.products.filter(p => p.kind !== 'giftcard' && !draft.items.some(i => i.productId === p.id));
  const name = account.customer.firstName;
  const set = (patch: Partial<SkinPlan>) => buffer.edit(d => ({
    ...d,
    ...patch
  }));
  const setItem = (i: number, patch: Partial<PlanItem>) => set({
    items: draft.items.map((x, j) => j === i ? {
      ...x,
      ...patch
    } : x)
  });
  const addProducts = (ids: string[]) => {
    const added = ids.map(id => index.productById(id)).filter((p): p is NonNullable<typeof p> => Boolean(p)).map(p => planItemFor(p, today()));
    set({
      items: sortPlanItems([...draft.items, ...added], index.productById)
    });
  };
  const save = async () => {
    setSaving(true);
    await buffer.commit(ctl.savePlan);
    setSaving(false);
  };
  return <div className="grid gap-6 lg:grid-cols-3" data-id="element-1447" data-mp="18i61g7bn291c">
      <div className="min-w-0 space-y-6 lg:col-span-2" data-id="element-1448" data-mp="18guyuatxj7bt">
        <Panel title="Producten in volgorde van gebruik" description={`Zo staat het in ${name}s huidplan. Nieuwe producten krijgen jouw standaardadvies; pas het aan waar nodig.`} flush data-id="element-1449" data-mp="2szyu7jx5kf31">
          {draft.items.length === 0 ? <div className="p-4 sm:p-5" data-id="element-1450" data-mp="2j8ca0y2u75o5">
              <EmptyState icon={WandSparklesIcon} title="Nog geen persoonlijk huidplan" text={provisional.items.length ? `${name} ziet nu een voorlopige routine uit haar bestellingen. Neem die over als startpunt, of kies een protocol.` : `${name} heeft nog niets besteld. Kies een protocol of voeg producten toe.`} action={provisional.items.length ? <button type="button" onClick={() => set({
            items: provisional.items
          })} className={btnPrimary} data-id="element-1452" data-mp="3uoj1188zvn7f">
                      Neem voorlopige routine over
                    </button> : undefined} data-id="element-1451" data-mp="2ebu9ybigbvi5" />
            </div> : <ol className="divide-y divide-line" data-id="element-1453" data-mp="2rey693hmzswr">
              {draft.items.map((item, i) => <PlanItemRow key={item.productId} item={item} step={i + 1} isFirst={i === 0} isLast={i === draft.items.length - 1} onChange={patch => setItem(i, patch)} onMove={dir => set({
            items: move(draft.items, i, i + dir)
          })} onRemove={() => set({
            items: draft.items.filter((_, j) => j !== i)
          })} data-id="element-1454" data-mp="2dfoqu61992tu" data-mpi={i} />)}
            </ol>}
          <div className="flex flex-wrap items-center gap-2 border-t border-line px-4 py-3 sm:px-5" data-id="element-1455" data-mp="1zojwnjgnp2qw">
            <select value="" onChange={e => e.target.value && addProducts([e.target.value])} aria-label="Product toevoegen" className={`${inputCls} sm:max-w-xs`} data-id="element-1456" data-mp="1kr9ghwt2j8ou">
              <option value="" data-id="element-1457" data-mp="165lt82rkjo1r">+ Product toevoegen…</option>
              {addable.map(p => <option key={p.id} value={p.id} data-id="element-1458" data-mp="1240kmtm2hvxd">
                  {p.brand} — {p.name}
                </option>)}
            </select>
            {missingFromRoutine.length > 0 && <button type="button" onClick={() => addProducts(missingFromRoutine)} className={btnGhost} data-id="element-1459" data-mp="1b4oxvm3mzs97">
                <WandSparklesIcon className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" data-id="element-1460" data-mp="1p00s7sdvrgpl" />
                Vul aan uit {routine?.name} ({missingFromRoutine.length})
              </button>}
          </div>
        </Panel>
      </div>

      <div className="min-w-0 space-y-6" data-id="element-1461" data-mp="1tu50ceyvtus9">
        <Panel title="Het plan" description={`Bovenaan ${name}s huidplan.`} data-id="element-1462" data-mp="1tv6n3gp9g5pt">
          <div className="space-y-5" data-id="element-1463" data-mp="28qw0iqgdraix">
            <Field label="Protocol" hint="Bepaalt de naam boven het plan en de koppeling met behandelingen." data-id="element-1464" data-mp="23r9qepvumaec">
              <select value={draft.routineId ?? ''} onChange={e => set({
              routineId: e.target.value || undefined
            })} className={inputCls} data-id="element-1465" data-mp="2aurdv3gjc9pj">
                <option value="" data-id="element-1466" data-mp="2fuc9b3whxzxi">Geen protocol</option>
                {catalog.routines.map(r => <option key={r.id} value={r.id} data-id="element-1467" data-mp="2kr2si03llbn5">
                    {r.name}
                  </option>)}
              </select>
            </Field>
            <Field label="Waar we aan werken" hint="Kort, in haar woorden. Bijv. “Pigment op de wangen”." data-id="element-1468" data-mp="2uj8hr22gteyn">
              <input value={draft.focus} onChange={e => set({
              focus: e.target.value
            })} className={inputCls} data-id="element-1469" data-mp="2yjain90dm4cv" />
            </Field>
            <Field label={`Notitie voor ${name}`} hint="Ziet ze onder haar huidplan. Interne notities horen bij Overzicht." data-id="element-1470" data-mp="33jy9nuolul73">
              <textarea value={draft.note} onChange={e => set({
              note: e.target.value
            })} rows={4} className={`${inputCls} resize-y leading-relaxed`} data-id="element-1471" data-mp="38fvofwk7zjhr" />
            </Field>
            <Field label="Plan gestart op" data-id="element-1472" data-mp="1e4h11meolw56">
              <input type="date" value={draft.startedOn} onChange={e => set({
              startedOn: e.target.value
            })} className={`${inputCls} max-w-[12rem]`} data-id="element-1473" data-mp="16kfwukkek3qw" />
            </Field>
          </div>
        </Panel>

        <div className="flex flex-wrap items-center gap-2" data-id="element-1474" data-mp="wud32i5gilri">
          <button type="button" onClick={save} disabled={!dirty || saving} className={btnPrimary} data-id="element-1475" data-mp="wss8wtn214u6">
            {saving ? 'Bewaren…' : 'Huidplan bewaren'}
          </button>
          {dirty && <button type="button" onClick={() => {
          // Terugzetten gooit onbewaarde invoer weg: die blijft een paar seconden terug te halen.
          const unsaved = clone(draft);
          buffer.discard();
          undoToast('Huidplan teruggezet naar de bewaarde versie', () => buffer.restore(unsaved), {
            restored: 'Je wijzigingen staan er weer'
          });
        }} className={btnSecondary} data-id="element-1476" data-mp="1phhcr0fhvo9m">
              Terugzetten
            </button>}
        </div>
        {buffer.external && <p role="status" className="text-[13px] text-clay" data-id="element-1477" data-mp="otoynjy5sq05">
            Het huidplan is intussen ergens anders aangepast. Bewaren zet jouw versie eroverheen; Terugzetten neemt die andere over.
          </p>}
        <p className="text-[12px] leading-relaxed text-muted" data-id="element-1478" data-mp="fwoejpi4he2i">
          Wordt direct bij {name} bewaard — los van “Opslaan” bovenin, dat alleen over de website gaat.
        </p>
      </div>
    </div>;
}